import { FC, useCallback, useState } from "react";

import { t } from "i18n/i18n";
import { IconText } from "mds/components/IconText";
import { SquareButton } from "mds/components/buttons/SquareButton";
import { ArrowDownToLineIcon, LoadingDotsIcon } from "mds/icons";
import { useAppSelector } from "store/index";
import {
  selectAllVisibleClassSessionsForCurrentSection,
  selectCurrentSectionTitle,
  selectOrgUserByUserIdMap,
  selectStudentClassSessionUserSummaries,
  userSelectors,
} from "store/selectors";
import { toastErrorMessage } from "utils/alerts";
import { downloadCSVFile } from "utils/file";
import { generateAttendanceReportCSVFromSessions } from "utils/reporting";

export const AttendanceReportButton: FC = () => {
  const sectionTitle = useAppSelector(selectCurrentSectionTitle);
  const allVisibleClassSessions = useAppSelector(selectAllVisibleClassSessionsForCurrentSection);
  const studentClassSessionUserSummaries = useAppSelector(selectStudentClassSessionUserSummaries);
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const users = useAppSelector(userSelectors.selectEntities);
  const orgUsersMap = useAppSelector(selectOrgUserByUserIdMap);

  const handleDownloadAttendanceReport = useCallback(() => {
    if (!allVisibleClassSessions || allVisibleClassSessions.length === 0) {
      toastErrorMessage(t("session_log_view.export.no_data"));
      return;
    }

    if (!sectionTitle) {
      toastErrorMessage(t("session_log_view.export.failed"));
      return;
    }

    setIsGeneratingReport(true);
    try {
      const { csvData, filename } = generateAttendanceReportCSVFromSessions(
        allVisibleClassSessions,
        users,
        orgUsersMap,
        sectionTitle,
        studentClassSessionUserSummaries,
      );
      downloadCSVFile(csvData, filename);
    } catch {
      toastErrorMessage(t("session_log_view.export.failed"));
    } finally {
      setIsGeneratingReport(false);
    }
  }, [allVisibleClassSessions, users, sectionTitle, orgUsersMap, studentClassSessionUserSummaries]);

  return (
    <SquareButton
      disabled={
        !allVisibleClassSessions.length ||
        !sectionTitle ||
        !studentClassSessionUserSummaries.length ||
        isGeneratingReport
      }
      kind="tertiary"
      size="s"
      onClick={handleDownloadAttendanceReport}
    >
      <IconText
        iconStart={isGeneratingReport ? <LoadingDotsIcon /> : <ArrowDownToLineIcon />}
        text={t("session_log_view.attendance_report.download_button")}
      />
    </SquareButton>
  );
};
